import { Segment } from '@mairistem/composants';
import { ListeTable } from '@mairistem/liste';
import React from 'react';
import { toast } from 'react-toastify';
import STATUT_PROJET from '../../constants/common/statutProjet';
import type Projet from '../../interfaces/common/projet';
import { searchProjet } from '../../services/projet';
import type { AutorisationFiltersData } from './AutorisationFilters';
import AutorisationListRow from './AutorisationListRow';
import './AutorisationList.css';

interface AutorisationListMeta {
	lastPage: number;
	total: number;
}

interface AutorisationListProps {
	decision: number;
	filters: AutorisationFiltersData;
}

const defaultListeMeta = { lastPage: 0, total: 0 };

const AutorisationList = ({ decision, filters }: AutorisationListProps) => {
	const [autorisations, setAutorisations] = React.useState<Projet[]>([]);
	const [listMeta, setListMeta] =
		React.useState<AutorisationListMeta>(defaultListeMeta);
	const [loading, setLoading] = React.useState(true);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Pas besoin de 'decision'
	const fetchAutorisations = React.useCallback(
		async (page: number) => {
			try {
				setLoading(true);
				const {
					data,
					meta: { lastPage, total },
				} = await searchProjet(
					new URLSearchParams({
						...(filters.q && { q: filters.q }),
						...(filters.statut !== null &&
							filters.statut !== STATUT_PROJET.TOUTES && {
								statut: String(filters.statut),
							}),
						decision: String(decision),
						page: String(page),
						withEtiquette: 'true',
					}),
				);
				setAutorisations((old) => (page === 1 ? data : [...old, ...data]));
				setListMeta({ lastPage, total });
			} catch (_e) {
				toast.error('Erreur lors du chargement des autorisations de programme');
			} finally {
				setLoading(false);
			}
		},
		[filters.q, filters.statut],
	);

	const handleFinish = (data: Projet) => {
		setAutorisations((old) =>
			old.map((autorisation) =>
				autorisation.identifiant === data.identifiant ? data : autorisation,
			),
		);
	};

	const handleLoad = (args) => {
		fetchAutorisations(args.page);
	};

	React.useEffect(() => {
		fetchAutorisations(1);
	}, [fetchAutorisations]);

	return (
		<Segment className="autorisationListSegment">
			<ListeTable
				count={listMeta.total}
				items={autorisations}
				loading={loading}
				onLoad={handleLoad}
				page={listMeta.lastPage}
				perPage={15}
				striped={false}
			>
				<ListeTable.Header>
					<ListeTable.Column name="libelle" width={5}>
						Libellé
					</ListeTable.Column>
					<ListeTable.Column name="categorie" width={3}>
						Catégorie
					</ListeTable.Column>
					<ListeTable.Column name="montantAutorisation" width={2}>
						Montant total
					</ListeTable.Column>
					<ListeTable.Column name="dateVote" width={2}>
						Date de délibération
					</ListeTable.Column>
					<ListeTable.Column name="statut" width={3}>
						Statut
					</ListeTable.Column>
					<ListeTable.Column name="actions" width={1}>
						Actions
					</ListeTable.Column>
				</ListeTable.Header>
				{
					((autorisation: Projet) => (
						<AutorisationListRow
							autorisation={autorisation}
							decision={decision}
							onFinish={handleFinish}
						/>
					)) as unknown as React.ReactNode
				}
			</ListeTable>
		</Segment>
	);
};

export default AutorisationList;
